import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Users, Search, PawPrint, CalendarCheck, Download, Eye, FileText, MoreHorizontal, Tag, StickyNote } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminClients() {
  const [clients, setClients] = useState([]);
  const [pets, setPets] = useState([]);
  const [search, setSearch] = useState('');
  const [speciesFilter, setSpeciesFilter] = useState('all');
  const [breedFilter, setBreedFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [loading, setLoading] = useState(true);
  const [detailDialog, setDetailDialog] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientDetail, setClientDetail] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [tagName, setTagName] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        const [c, p] = await Promise.all([api.get('/admin/clients'), api.get('/admin/pets')]);
        setClients(c.data);
        setPets(p.data);
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    load();
  }, []);

  // Get unique breeds from pets
  const allBreeds = [...new Set(pets.map(p => p.breed).filter(Boolean))].sort();

  // Filter clients
  const getClientPets = (userId) => pets.filter(p => p.owner_id === userId);

  const filtered = clients.filter(c => {
    const matchSearch = !search || c.name?.toLowerCase().includes(search.toLowerCase()) || c.email?.toLowerCase().includes(search.toLowerCase()) || c.phone?.includes(search);
    const clientPets = getClientPets(c.user_id);
    const matchSpecies = speciesFilter === 'all' || clientPets.some(p => p.species === speciesFilter);
    const matchBreed = breedFilter === 'all' || clientPets.some(p => p.breed === breedFilter);
    return matchSearch && matchSpecies && matchBreed;
  }).sort((a, b) => {
    if (sortBy === 'name') return (a.name || '').localeCompare(b.name || '');
    if (sortBy === 'bookings') return (b.booking_count || 0) - (a.booking_count || 0);
    if (sortBy === 'pets') return (b.pet_count || 0) - (a.pet_count || 0);
    if (sortBy === 'newest') return (b.created_at || '').localeCompare(a.created_at || '');
    return 0;
  });

  const viewDetail = async (client) => {
    setSelectedClient(client);
    try {
      const res = await api.get(`/admin/clients/${client.user_id}/detail`);
      setClientDetail(res.data);
      setDetailDialog(true);
    } catch { toast.error('Failed to load client details'); }
  };

  const addNote = async () => {
    if (!noteText.trim() || !selectedClient) return;
    try {
      await api.post(`/admin/clients/${selectedClient.user_id}/notes`, { text: noteText });
      toast.success('Note added');
      setNoteText('');
      const res = await api.get(`/admin/clients/${selectedClient.user_id}/detail`);
      setClientDetail(res.data);
    } catch { toast.error('Failed to add note'); }
  };

  const addTag = async () => {
    if (!tagName.trim() || !selectedClient) return;
    const colors = ['#4A90E2', '#E07A5F', '#4A7C59', '#7C3AED', '#E8B931', '#D9534F'];
    try {
      await api.post(`/admin/clients/${selectedClient.user_id}/tags`, { name: tagName, color: colors[Math.floor(Math.random() * colors.length)] });
      toast.success('Tag added');
      setTagName('');
      const res = await api.get(`/admin/clients/${selectedClient.user_id}/detail`);
      setClientDetail(res.data);
    } catch { toast.error('Failed to add tag'); }
  };

  const removeTag = async (tagId) => {
    try {
      await api.delete(`/admin/clients/${selectedClient.user_id}/tags/${tagId}`);
      const res = await api.get(`/admin/clients/${selectedClient.user_id}/detail`);
      setClientDetail(res.data);
    } catch { toast.error('Failed'); }
  };

  const exportAll = async () => {
    try {
      const res = await api.get('/admin/clients/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'clients.csv';
      a.click();
      toast.success('Clients exported');
    } catch { toast.error('Export failed'); }
  };

  const exportSingle = async (userId, name) => {
    try {
      const res = await api.get(`/admin/clients/${userId}/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `client_${name.replace(/\s/g, '_').toLowerCase()}.csv`;
      a.click();
      toast.success('Client data exported');
    } catch { toast.error('Export failed'); }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[#2D2D2D] border-t-transparent" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-clients-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#1A1A1A]" style={{ fontFamily: 'Manrope' }}>Client Database</h1>
          <p className="text-sm text-[#5A5A5A] mt-1">{filtered.length} of {clients.length} clients</p>
        </div>
        <Button variant="outline" className="rounded-full" onClick={exportAll} data-testid="export-clients-btn">
          <Download className="w-4 h-4 mr-2" /> Export All CSV
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#888]" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, email, phone..." className="pl-10 rounded-lg" data-testid="client-search-input" />
        </div>
        <Select value={speciesFilter} onValueChange={setSpeciesFilter}>
          <SelectTrigger className="w-32 rounded-lg" data-testid="species-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Species</SelectItem>
            <SelectItem value="dog">Dogs</SelectItem>
            <SelectItem value="cat">Cats</SelectItem>
          </SelectContent>
        </Select>
        <Select value={breedFilter} onValueChange={setBreedFilter}>
          <SelectTrigger className="w-44 rounded-lg" data-testid="breed-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Breeds</SelectItem>
            {allBreeds.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-36 rounded-lg" data-testid="sort-by"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="name">Sort: Name</SelectItem>
            <SelectItem value="bookings">Sort: Bookings</SelectItem>
            <SelectItem value="pets">Sort: Pets</SelectItem>
            <SelectItem value="newest">Sort: Newest</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Client Table */}
      <Card className="rounded-2xl border-[#E5E5E0] bg-white overflow-hidden" style={{ boxShadow: 'var(--shadow-soft)' }}>
        {filtered.length === 0 ? (
          <CardContent className="p-8 text-center"><Users className="w-10 h-10 text-[#D6D6D0] mx-auto mb-3" /><p className="text-sm text-[#5A5A5A]">No clients match your filters</p></CardContent>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F9F9F7]">
                  <TableHead className="text-xs font-semibold text-[#5A5A5A]">Name</TableHead>
                  <TableHead className="text-xs font-semibold text-[#5A5A5A]">Email</TableHead>
                  <TableHead className="text-xs font-semibold text-[#5A5A5A]">Phone</TableHead>
                  <TableHead className="text-xs font-semibold text-[#5A5A5A]">Pets</TableHead>
                  <TableHead className="text-xs font-semibold text-[#5A5A5A]">Bookings</TableHead>
                  <TableHead className="text-xs font-semibold text-[#5A5A5A]">Joined</TableHead>
                  <TableHead className="text-xs font-semibold text-[#5A5A5A]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(c => (
                  <TableRow key={c.user_id} className="hover:bg-[#F9F9F7]/50" data-testid={`client-row-${c.user_id}`}>
                    <TableCell className="text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-[#E2E8E4] rounded-full flex items-center justify-center text-xs font-semibold">{c.name?.[0]}</div>
                        <div>
                          <span>{c.name}</span>
                          {c.tags?.length > 0 && (
                            <div className="flex gap-1 mt-0.5">
                              {c.tags.map(t => <span key={t.tag_id} className="text-[10px] px-1.5 py-0.5 rounded-full text-white" style={{ backgroundColor: t.color }}>{t.name}</span>)}
                            </div>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-[#5A5A5A]">{c.email}</TableCell>
                    <TableCell className="text-sm text-[#5A5A5A]">{c.phone || '-'}</TableCell>
                    <TableCell><span className="flex items-center gap-1 text-sm"><PawPrint className="w-3 h-3" />{c.pet_count}</span></TableCell>
                    <TableCell><span className="flex items-center gap-1 text-sm"><CalendarCheck className="w-3 h-3" />{c.booking_count}</span></TableCell>
                    <TableCell className="text-xs text-[#888]">{c.created_at?.split('T')[0]}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <button onClick={() => viewDetail(c)} className="p-1.5 rounded-lg hover:bg-[#F5F5F0]" title="View Details" data-testid={`view-client-${c.user_id}`}>
                          <Eye className="w-3.5 h-3.5 text-[#888]" />
                        </button>
                        <button onClick={() => exportSingle(c.user_id, c.name)} className="p-1.5 rounded-lg hover:bg-[#F5F5F0]" title="Export" data-testid={`export-client-${c.user_id}`}>
                          <FileText className="w-3.5 h-3.5 text-[#888]" />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      {/* Client Detail Dialog */}
      <Dialog open={detailDialog} onOpenChange={setDetailDialog}>
        <DialogContent className="rounded-2xl max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Manrope' }}>{clientDetail?.client?.name || 'Client Details'}</DialogTitle></DialogHeader>
          {clientDetail && (
            <div className="space-y-5">
              {/* Client Info */}
              <div className="bg-[#F9F9F7] rounded-xl p-4 space-y-1">
                <p className="text-sm"><span className="text-[#888]">Email:</span> {clientDetail.client.email}</p>
                <p className="text-sm"><span className="text-[#888]">Phone:</span> {clientDetail.client.phone || '-'}</p>
                <p className="text-sm"><span className="text-[#888]">Joined:</span> {clientDetail.client.created_at?.split('T')[0]}</p>
              </div>

              {/* Tags */}
              <div>
                <p className="text-xs font-semibold text-[#888] uppercase mb-2 flex items-center gap-1"><Tag className="w-3 h-3" /> Tags</p>
                <div className="flex flex-wrap gap-2 mb-2">
                  {clientDetail.tags?.map(t => (
                    <span key={t.tag_id} className="text-xs px-2.5 py-1 rounded-full text-white flex items-center gap-1" style={{ backgroundColor: t.color }}>
                      {t.name}
                      <button onClick={() => removeTag(t.tag_id)} className="ml-0.5 opacity-70 hover:opacity-100">&times;</button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input value={tagName} onChange={e => setTagName(e.target.value)} placeholder="Add tag..." className="rounded-lg text-sm h-8 flex-1" data-testid="add-tag-input" />
                  <Button size="sm" className="rounded-lg bg-[#2D2D2D] text-white h-8 text-xs" onClick={addTag} data-testid="add-tag-btn">Add</Button>
                </div>
              </div>

              {/* Pets */}
              <div>
                <p className="text-xs font-semibold text-[#888] uppercase mb-2 flex items-center gap-1"><PawPrint className="w-3 h-3" /> Pets ({clientDetail.pets?.length})</p>
                <div className="grid grid-cols-2 gap-2">
                  {clientDetail.pets?.map(p => (
                    <div key={p.pet_id} className="bg-[#F5F5F0] rounded-lg p-2.5">
                      <p className="text-sm font-medium">{p.name}</p>
                      <p className="text-xs text-[#888]">{p.breed} ({p.species}) | {p.weight}kg</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Notes */}
              <div>
                <p className="text-xs font-semibold text-[#888] uppercase mb-2 flex items-center gap-1"><StickyNote className="w-3 h-3" /> Internal Notes</p>
                {clientDetail.notes?.length > 0 && (
                  <div className="space-y-2 mb-2">
                    {clientDetail.notes.map(n => (
                      <div key={n.note_id} className="bg-[#FEFCE8] rounded-lg p-2.5">
                        <p className="text-sm">{n.text}</p>
                        <p className="text-xs text-[#888] mt-1">by {n.created_by_name} on {n.created_at?.split('T')[0]}</p>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex gap-2">
                  <Input value={noteText} onChange={e => setNoteText(e.target.value)} placeholder="Add note..." className="rounded-lg text-sm h-8 flex-1" data-testid="add-note-input" />
                  <Button size="sm" className="rounded-lg bg-[#2D2D2D] text-white h-8 text-xs" onClick={addNote} data-testid="add-note-btn">Add</Button>
                </div>
              </div>

              {/* Recent Bookings */}
              <div>
                <p className="text-xs font-semibold text-[#888] uppercase mb-2">Recent Bookings ({clientDetail.bookings?.length})</p>
                <div className="space-y-1.5 max-h-40 overflow-y-auto">
                  {clientDetail.bookings?.slice(0, 5).map(b => (
                    <div key={b.appointment_id} className="flex items-center justify-between bg-[#F9F9F7] rounded-lg p-2">
                      <div>
                        <p className="text-sm">{b.date} at {b.time_slot}</p>
                        <p className="text-xs text-[#888]">{b.service_names?.join(', ')} | {b.staff_name}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium">&#8377;{b.total_price}</p>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full ${b.status === 'completed' ? 'bg-green-100 text-green-800' : b.status === 'cancelled' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'}`}>{b.status}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
